/* Floating search keeps the native Shopify result markup without drawer scroll locks. */
if (!customElements.get('gk-floating-search')) {
  customElements.define('gk-floating-search', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-js-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.clear = this.querySelector('[data-search-clear]');
      this.footer = this.querySelector('[data-search-footer]');
      this.all = this.querySelector('[data-search-all]');
      this.cache = new Map();
      this.sequence = 0;
      this.resize = () => this.position();
      this.querySelector('[data-search-close]').addEventListener('click', () => this.hide(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.hide(); }, options);
      this.dialog.addEventListener('close', () => this.release(), options);
      this.dialog.addEventListener('pointerdown', event => { this.outsideStart = this.isOutside(event); }, options);
      this.dialog.addEventListener('click', event => {
        if (this.outsideStart && this.isOutside(event)) this.hide();
      }, options);
      this.input.addEventListener('input', () => this.schedule(), options);
      this.input.addEventListener('compositionend', () => this.schedule(), options);
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.schedule();
        this.input.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('keydown', event => this.keyboard(event), options);
      this.querySelector('form').addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
      }, options);
      window.addEventListener('pagehide', () => this.hide(false), options);
    }

    isOutside(event) {
      const box = this.dialog.getBoundingClientRect();
      return event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
    }

    show() {
      if (this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.trigger = document.getElementById('search-open-button');
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.anchorBottom = document.querySelector('main-header')?.getBoundingClientRect().bottom || 76;
      this.savedStyles = [];
      const set = (element, property, value) => {
        this.savedStyles.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
        element.style.setProperty(property, value);
      };
      const body = document.body;
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (scrollbar) set(body, 'padding-right', `${parseFloat(getComputedStyle(body).paddingRight) + scrollbar}px`);
      set(document.documentElement, 'overflow', 'hidden');
      set(body, 'position', 'fixed');
      set(body, 'top', `${-this.scrollPosition.y}px`);
      set(body, 'left', `${-this.scrollPosition.x}px`);
      set(body, 'width', '100%');
      set(body, 'overflow', 'hidden');
      this.position();
      this.dialog.showModal();
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
      window.addEventListener('resize', this.resize);
      window.visualViewport?.addEventListener('resize', this.resize);
      window.visualViewport?.addEventListener('scroll', this.resize);
      if (this.input.value.trim()) this.schedule();
    }

    position() {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const offset = viewport?.offsetTop || 0;
      const top = offset + (height < 500 ? 12 : Math.max(12, Math.min(this.anchorBottom + 12, 180, height - 220)));
      this.dialog.style.setProperty('--gk-search-top', `${top}px`);
      this.dialog.style.setProperty('--gk-search-height', `${Math.max(140, height - (top - offset) - 16)}px`);
    }

    hide(restoreFocus = true) {
      this.restoreFocus = restoreFocus;
      if (this.dialog.open) this.dialog.close();
      this.release();
    }

    release() {
      if (!this.savedStyles) return;
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
      window.removeEventListener('resize', this.resize);
      window.visualViewport?.removeEventListener('resize', this.resize);
      window.visualViewport?.removeEventListener('scroll', this.resize);
      this.savedStyles.forEach(([element, property, value, priority]) => {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      });
      this.savedStyles = null;
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.restoreFocus !== false && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.restoreFocus = true;
    }

    message(key, quiet = false) {
      this.status.textContent = this.dataset[key];
      this.status.toggleAttribute('data-quiet', quiet);
    }

    schedule() {
      clearTimeout(this.timer);
      this.request?.abort();
      const sequence = ++this.sequence;
      const query = this.input.value.trim();
      this.clear.hidden = !this.input.value;
      this.results.replaceChildren();
      this.footer.hidden = !query;
      this.results.removeAttribute('aria-busy');
      if (!query) { this.message('idle'); return; }
      const url = new URL(this.querySelector('form').action);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      this.all.href = url.href;
      this.message('loading');
      this.results.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.search(query, sequence), 220);
    }

    async search(query, sequence) {
      try {
        let html = this.cache.get(query);
        if (!html) {
          this.request = new AbortController();
          const url = new URL(this.dataset.predictiveUrl, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('section_id', 'helper-predictive-search');
          const response = await fetch(url, { signal: this.request.signal });
          if (!response.ok) throw new Error('Search unavailable');
          html = await response.text();
          if (sequence !== this.sequence) return;
          if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(query, html);
        }
        if (sequence !== this.sequence || !this.dialog.open) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const holder = parsed.querySelector('.sidebar__search-results');
        if (!holder) throw new Error('Missing search results');
        // Put product matches before Shopify's leading keyword suggestions on phones.
        const leadingSuggestions = [];
        const firstHeading = holder.querySelector('.sidebar__caption');
        if (firstHeading) {
          while (holder.firstElementChild && holder.firstElementChild !== firstHeading) {
            leadingSuggestions.push(holder.firstElementChild);
            holder.firstElementChild.remove();
          }
          if (leadingSuggestions.length) {
            const heading = document.createElement('span');
            heading.className = 'sidebar__caption';
            heading.textContent = this.dataset.suggestions;
            holder.append(heading, ...leadingSuggestions);
          }
        }
        holder.querySelectorAll('.content a').forEach(link => link.removeAttribute('tabindex'));
        this.results.replaceChildren(...holder.childNodes);
        this.results.scrollTop = 0;
        const hasResults = !!this.results.querySelector('a');
        this.message(hasResults ? 'ready' : 'empty', hasResults);
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) this.message('error');
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    keyboard(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
        return;
      }
      const links = [...this.results.querySelectorAll('.content a[href]')];
      const index = links.indexOf(document.activeElement);
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && (event.target === this.input || index >= 0)) {
        if (!links.length) return;
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? Math.min(index + 1, links.length - 1) : index - 1;
        if (next < 0) this.input.focus({ preventScroll: true });
        else { links[next].focus({ preventScroll: true }); links[next].scrollIntoView({ block: 'nearest' }); }
      }
      if (event.key === 'Tab') {
        const focusable = [...this.dialog.querySelectorAll('button, input:not([type="hidden"]), a[href]')].filter(element => element.tabIndex >= 0 && element.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
      }
    }

    disconnectedCallback() {
      this.hide(false);
      this.events?.abort();
      this.events = null;
    }
  });
}
